import React from "react";
import { Stack, useTheme } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import ColumnItem from "./ColumnItem";
import { ColumnDialogParams, ColumnModelItem } from "components/tables/columns/ColumnModel";

interface ColumnListProps {
  // Array of columns currently selected
  activeColumns: ColumnModelItem[] | [];
  // Moves a single column down
  moveColumnDown: (colIndex: number) => void;
  // Moves a single column up
  moveColumnUp: (colIndex: number) => void;
  onColumnDialogMode?: (params: ColumnDialogParams) => void;
  // Handles column sorting when dragging
  onDragColumn(sortedColumns: ColumnModelItem[] | []): void;
  onRemoveColumn: (columnItem: ColumnModelItem) => void;
  /**
   * Object representing sorted column and sort direction - based on MUI's column state
   * See {@link https://mui.com/x/react-data-grid/sorting/#structure-of-the-model}
   */
  sortModel?: GridSortModel | [];
  // Toggles the active state of a single column
  toggleActiveColumn: (column: ColumnModelItem) => void;
  // Array of objects representing the visible column's properties.
  visibleColumns: ColumnModelItem[] | [];
}

export default function ColumnList(props: ColumnListProps) {
  const theme = useTheme();
  const sortableItems = props.visibleColumns.map((col, index) => `${col.field}_${index}`);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || (over.id === active.id)) {
      return;
    }

    const oldIndex = sortableItems.indexOf(active.id.toString());
    const newIndex = sortableItems.indexOf(over.id.toString());
    props.onDragColumn(arrayMove(props.visibleColumns, oldIndex, newIndex));
  }

  return (
    <Stack
      sx={{
        flexGrow: 0,
        gap: 1,
        padding: 1,
        background: theme.palette.background.default
      }}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sortableItems}
          strategy={verticalListSortingStrategy}
        >
          {props.visibleColumns.map((column: ColumnModelItem, index: number) => {
            const { field, headerName, hasHeaderOptions } = column;
            const isColumnActive = props.activeColumns.some(col => col.field === column.field);
            const key = `${field}_${index}`;

            return (
              <ColumnItem
                key={key}
                id={key}
                columnItem={column}
                colIndex={index}
                colListLength={props.visibleColumns.length}
                field={field}
                onColumnDialogMode={props.onColumnDialogMode}
                onRemoveColumn={props.onRemoveColumn}
                hasHeaderOptions={hasHeaderOptions}
                headerName={headerName}
                isColumnActive={isColumnActive}
                moveColumnDown={() => props.moveColumnDown(index)}
                moveColumnUp={() => props.moveColumnUp(index)}
                toggleActiveColumn={() => props.toggleActiveColumn(column)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </Stack>
  );
}

